"use client";

import React from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card } from "@/components/ui/card";
const timelineData = [{
  decade: "1930s",
  title: "The Beginning",
  description: "Opening its doors in 1938, the Elbo Room quickly became a staple of Fort Lauderdale beach, offering a simple, open-air escape for locals and early tourists seeking the ocean breeze.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1587258/img/elbo_room_1930s_archival.png"
}, {
  decade: "1960s",
  title: "Where the Boys Are",
  description: "Catapulted to international fame by the 1960 film 'Where the Boys Are', cementing its status as the ultimate Spring Break destination and a cultural touchstone of the era.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1587258/img/elbo_room_1960s_springbreak.png"
}, {
  decade: "1980s",
  title: "The Neon Era",
  description: "Embracing the vibrant energy of the 80s, the bar expanded its live music offerings, becoming a beacon of neon light on the A1A strip that never slept.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1587258/img/elbo_room_1980s_neon.png"
}, {
  decade: "2000s",
  title: "A Modern Classic",
  description: "Transitioning into a new millennium while fiercely protecting its historic charm, continuing to host daily live bands and serving generations of loyal patrons.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1587258/img/elbo_room_2000s_live.png"
}, {
  decade: "Present",
  title: "The Definitive Hub",
  description: "Today, it stands not just as a legendary bar, but as a high-octane media hub, broadcasting the spirit and sound of Fort Lauderdale to the world.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1587258/img/elbo_room_present_media.png"
}];
function TimelineNode({
  item,
  index
}: {
  item: (typeof timelineData)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const isEven = index % 2 === 0;
  return <div ref={ref} data-index={index} className={`relative flex w-full flex-col items-center justify-between mb-24 last:mb-0 transition-all duration-1000 md:flex-row ${isVisible ? "translate-y-0 opacity-100" : "translate-y-16 opacity-0"} ${isEven ? "md:flex-row-reverse" : ""}`}>
      {}
      <div className="absolute left-4 z-10 h-4 w-4 -translate-x-1/2 rounded-full border-2 border-background bg-primary shadow-[0_0_15px_var(--color-primary)] md:left-1/2" />

      {}
      <div className={`flex w-full flex-col pl-12 md:w-[45%] md:pl-0 ${isEven ? "md:items-start md:text-left" : "md:items-end md:text-right"}`}>
        <h3 className="mb-2 font-serif text-5xl tracking-widest text-primary drop-shadow-[0_0_10px_rgba(var(--color-primary),0.8)] md:text-7xl">
          {item.decade}
        </h3>
        <h4 className="mb-4 text-2xl font-bold text-foreground uppercase tracking-wide">
          {item.title}
        </h4>
        <p className="font-sans text-lg leading-relaxed text-muted-foreground">
          {item.description}
        </p>
      </div>

      {}
      <div className="mt-8 w-full pl-12 md:mt-0 md:w-[45%] md:pl-0">
        <Card className="group relative overflow-hidden rounded-sm border-border/30 bg-card/10 p-2 backdrop-blur-md transition-colors hover:border-primary/50">
          <div className="relative overflow-hidden rounded-sm bg-muted">
            <div className="absolute inset-0 z-10 bg-primary/10 opacity-0 mix-blend-overlay transition-opacity duration-500 group-hover:opacity-100" />
            <img src={item.image} alt={`Elbo Room in the ${item.decade}`} className="h-64 w-full object-cover grayscale transition-all duration-700 ease-in-out group-hover:scale-105 group-hover:grayscale-0 md:h-80" />
          </div>
        </Card>
      </div>
    </div>;
}
export default function OurLegacy() {
  return <section id="our-legacy" className="dark relative overflow-hidden bg-background py-24 text-foreground md:py-32">
      {}
      <div className="pointer-events-none absolute inset-0 z-0 opacity-[0.03] mix-blend-overlay" style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
    }} />

      {}
      <div className="pointer-events-none absolute top-1/4 left-0 h-96 w-96 -translate-x-1/2 rounded-full bg-primary/5 blur-[120px]" />
      <div className="pointer-events-none absolute right-0 bottom-1/4 h-96 w-96 translate-x-1/2 rounded-full bg-secondary/10 blur-[120px]" />

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className="mb-20 text-center">
          <h2 className="font-serif text-5xl tracking-wider text-foreground md:text-7xl">
            Our <span className="text-primary drop-shadow-[0_0_15px_rgba(var(--color-primary),0.5)]">Legacy</span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl font-sans text-lg text-muted-foreground">
            Decades of history, immortalized in black and white, brought to life in vivid color.
          </p>
        </div>

        <div className="relative mx-auto max-w-6xl">
          {}
          <div className="absolute bottom-0 left-4 top-0 w-0.5 -translate-x-1/2 bg-primary/20 md:left-1/2">
            <div className="absolute top-0 left-0 h-full w-full bg-primary shadow-[0_0_10px_var(--color-primary)]" />
          </div>

          {}
          <div className="flex flex-col py-12">
            {timelineData.map((item, index) => <TimelineNode key={item.decade} item={item} index={index} />)}
          </div>
        </div>
      </div>
    </section>;
}